/**
 * ClaudeCodeHarness — wraps `@anthropic-ai/claude-agent-sdk` behind the
 * generic `AgentHarness` interface.
 *
 * This is the ONLY module that should import from the SDK.
 */

import { query, tool, createSdkMcpServer } from "@anthropic-ai/claude-agent-sdk";
import { z } from "zod";
import type {
  AgentHarness,
  HarnessEvent,
  HarnessQueryOptions,
  HarnessToolDefinition,
  HarnessMcpServer,
  HarnessUserMessage,
} from "../types.js";

export class ClaudeCodeHarness implements AgentHarness {
  /** The SDK stream is itself the structured-event source. */
  readonly emitsStructuredEvents = true;

  async *executeQuery(opts: {
    prompt: string | AsyncGenerator<HarnessUserMessage, void, unknown>;
    options: HarnessQueryOptions;
    resume?: string;
  }): AsyncGenerator<HarnessEvent, void> {
    const sdkEvents = query({
      prompt: opts.prompt as Parameters<typeof query>[0]["prompt"],
      options: {
        ...(opts.options as Parameters<typeof query>[0]["options"]),
        ...(opts.resume ? { resume: opts.resume } : {}),
        ...(opts.options.sessionId ? { sessionId: opts.options.sessionId } : {}),
        ...(opts.options.abortController ? { abortController: opts.options.abortController } : {}),
      },
    });

    for await (const event of sdkEvents) {
      yield event as unknown as HarnessEvent;
    }
  }

  createMcpServer(config: { name: string; tools: HarnessToolDefinition[] }): HarnessMcpServer {
    const sdkTools = config.tools.map((t) => {
      const sdkTool = tool(
        t.name,
        t.description,
        t.schema,
        t.handler as Parameters<typeof tool>[3],
        {
          ...(t.annotations ? { annotations: t.annotations } : {}),
          // Preloads the tool into the prompt (skips ToolSearch deferral) via
          // `_meta['anthropic/alwaysLoad']` on the SDK MCP transport.
          ...(t.alwaysLoad ? { alwaysLoad: true } : {}),
        },
      );
      if (t.strict) {
        // The SDK helper accepts only a raw shape and rebuilds a permissive
        // object schema. Its MCP server also accepts a complete Zod schema, so
        // preserve outer strictness by replacing the helper's shape.
        sdkTool.inputSchema = z.strictObject(t.schema) as never;
      }
      return sdkTool;
    });
    return createSdkMcpServer({ name: config.name, tools: sdkTools });
  }
}
